/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from './config.js';
/*
 * Links and URLs.
 *
 * The Map Viewer runs on three kinds of host — a platform subdomain, a
 * customer's own domain, and the dashboard under `/client/<slug>/` — and a
 * link written on one has to resolve on the others. Most of what follows is
 * about telling those apart.
 *
 * `externalLinkInNewTab` and `internalLinkTarget` were written for jQuery's
 * `.each`, which hands the element over as `this`. They take it as an
 * argument now and still accept the old binding, so a call site that has
 * not moved yet keeps working.
 */
/**
 * Checks if a specified URL leads to an external domain
 * @param {string} href URL string
 * @returns {boolean}
 */
export const isExternalLink = (href) => {
    const isProtocolRelative = /^\/\//.test(href);
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(href);
    const isRelative = !isProtocolRelative && !hasScheme;
    const isLocalhost =
        /^(https?:)?\/\/(localhost|127\.0\.0\.1|::1)(:\d{1,5})?/.test(href);
    const isInternalDomain =
        /^(https?:)?\/\/([^/]+\.)?(kontextmaps|kontextlab|derkontext)\.com(\/|$|[:?#])/i.test(
            href
        );
    if (currentHost().isClientDomain) {
        const url = new URL(href, window.location);
        return (
            url.hostname !== window.location.hostname &&
            !isRelative &&
            !isLocalhost &&
            !isInternalDomain
        );
    } else {
        return !isRelative && !isLocalhost && !isInternalDomain;
    }
};
/**
 * Strips the scheme and host from an absolute URL, returning only the path,
 * query, and fragment.
 * @param {string} href - An absolute or relative URL string.
 * @returns {string}
 */
export const getRelativeLink = (href) => href.replace(/^https?:\/\/[^/]+/, '');
/**
 * If the application is working on a client domain, this will return the map
 * slug. Otherwise, it will return an empty string.
 * @returns - The map slug
 * @type {() => string}
 */
export const getMapSlugFromLocation = () => {
    if (!currentHost().isClientDomain) {
        return '';
    }
    return window.location.pathname.split('/', 2)[1];
};
/**
 * Replaces the URL path of an internal URL by a rewritten version for client
 * domains.
 * @param url - An internal URL
 * @returns - The rewritten URL
 * @type {(url: string) => string}
 */
export const stripPathForApp = (url) => {
    if (!currentHost().isClientDomain) {
        return url;
    }
    const mapSlug = getMapSlugFromLocation();
    return url.replace(/\/client\/[^/]+\/\d+/, `/${mapSlug}`);
};
/**
 * Rewrites all internal URLs in a string with versions for a client domain.
 * @param {string} content A string
 * @returns {string} A rewritten version of the input string
 */
export const rewriteInternalUrlsForApp = (content) => {
    if (!currentHost().isClientDomain || !content) {
        return content;
    }
    const urlRegex =
        /(https:\/\/)?((\w+\.)?(kontextmaps|kontextlab|derkontext)\.com)?(\.\.\/?)*\/client\/[^/]+\/(\d+)(\/?[#?][^'"]*)?/g;
    const mapSlug = getMapSlugFromLocation();
    return content.replace(urlRegex, `/${mapSlug}$7`);
};
export const externalLinkInNewTab = function (element) {
    const elem = element ?? this;
    const href = elem.getAttribute('href');
    if (isExternalLink(href)) {
        elem.setAttribute('target', '_blank');
    }
};
export const internalLinkTarget = function (element) {
    const elem = element ?? this;
    const href = elem.getAttribute('href');
    if (!isExternalLink(href)) {
        try {
            const url = new URL(href, window.location);
            if (url.searchParams.has('_target')) {
                const target = `_${url.searchParams.get('_target')}`;
                url.searchParams.delete('_target');
                elem.setAttribute('href', url.toString());
                elem.setAttribute('target', target);
            }
        } catch (e) {
            console.error(e);
        }
    }
};
export const isCurrentMapURL = (link) => {
    const mapURL = window.location;
    const url = new URL(link, window.location);
    const slugPattern = '[a-z0-9]+[a-z0-9-]*';
    const mapIdPattern = `${slugPattern}|\\d+`;
    let mapPathMatch;
    if (currentHost().isClientDomain) {
        mapPathMatch = new RegExp(`^/(${mapIdPattern})`).exec(mapURL.pathname);
    } else {
        mapPathMatch = new RegExp(
            `^/client/(${slugPattern})/(${mapIdPattern})`
        ).exec(mapURL.pathname);
    }
    if (!mapPathMatch) return false;
    return (
        url.pathname === mapPathMatch[0] ||
        url.pathname.startsWith(`${mapPathMatch[0]}/`)
    );
};
/**
 *
 * @param url
 * @param addedParams {{string: string|null}}
 * @returns {*|string}
 */
export const injectParams = (url, addedParams) => {
    const addedParamNames = Object.keys(addedParams);
    if (window.URL) {
        const urlObj = new URL(url, window.location);
        for (const paramName of addedParamNames) {
            urlObj.searchParams.delete(paramName);
        }
        const paramEntries = Object.entries(addedParams);
        for (const param of paramEntries) {
            const [paramName, paramValue] = param;
            if (paramValue != null) {
                urlObj.searchParams.set(paramName, paramValue);
            }
        }
        urlObj.searchParams.sort();
        return urlObj.toString();
    } else {
        const addedParamEntries = addedParamNames.map((k) => {
            const v = addedParams[k];
            if (v != null) {
                return `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
            }
        });
        const paramParts = url.split('?', 2);
        const hasParams = paramParts.length > 1;
        const paramEntries = hasParams ? paramParts[1].split('&') : [];
        paramEntries.push(...addedParamEntries);
        paramEntries.sort();
        const hashParts = paramParts[0].split('#', 2);
        const hasHash = hashParts.length > 1;
        return (
            paramParts[0] +
            (paramEntries.length ? `?${paramEntries.join('&')}` : '') +
            (hasHash ? `#${hashParts[1]}` : '')
        );
    }
};
/**
 * Describe the host the Map Viewer is running on.
 *
 * Computed per call rather than once at import: `data-host-option` is set
 * by the server on the body, so reading it at module scope depends on when
 * the bundle happens to be evaluated.
 *
 * @returns {{isKontextmaps: boolean, isKontextlab: boolean,
 *   isDerKontext: boolean, oneOfOurs: boolean, isClientDomain: boolean}}
 */
export function currentHost() {
    const isKontextmapsDomain = window.location.hostname.match(
        /(\w+\.)?(kontextmaps\.com|localhost)/
    );
    const isKontextlabDomain = window.location.hostname.match(
        /(\w+\.)?(kontextlab\.com|localhost)/
    );
    const isDerKontextDomain = window.location.hostname.match(
        /(\w+\.)?(derkontext\.com|localhost)/
    );
    // The server says which application answered. The Jinja page put it
    // on `<body>`; the static shell has no such attribute, so the config
    // is the source and the attribute is only the fallback for a page
    // still rendered the old way. Reading the attribute alone made
    // `isClientDomain` permanently false on the shell, and every rewrite
    // for a customer domain quietly did nothing.
    const hostOption =
        getConfig()?.host?.option ?? document.body?.dataset?.hostOption;
    return {
        isKontextmaps: !!isKontextmapsDomain,
        isKontextlab: !!isKontextlabDomain,
        isDerKontext: !!isDerKontextDomain,
        oneOfOurs:
            !!isKontextmapsDomain ||
            !!isDerKontextDomain ||
            !!isKontextlabDomain,
        isClientDomain: hostOption === 'clientApp'
    };
}
/**
 * Report whether the Map Viewer is running inside a frame.
 *
 * @returns {boolean} Assumed true when the check itself is blocked: reading
 *   `window.top` across origins throws, and only a framed page can be in
 *   that position.
 */
export function isEmbedded() {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}
/**
 * Report whether the Map Viewer is running on a development host.
 *
 * @returns {boolean}
 */
export function isLocalhost() {
    return /^(localhost|127\.0\.0\.1|::1)$/i.test(
        window.location.hostname
    );
}

/**
 * Checks if a URL is reachable by sending a HEAD request.
 * @param {string} url - The URL to probe.
 * @returns {Promise<boolean>} Resolves to `true` when the server returns a 2xx response.
 */
export const urlExists = (url) =>
    fetch(url, { method: 'HEAD' })
        .then((response) => response.ok)
        .catch(() => false);
